/**
 * OverduePaymentsTab Component
 * Displays overdue payments with days overdue indicator
 */

import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/validation';
import ContractReferenceCell from '../contracts/ContractReferenceCell';
import SortableHeaderCell from '../common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';

const OVERDUE_COLUMNS = [
  { key: 'contract',     align: 'left',  getValue: (p) => p.contract_reference },
  { key: 'investor',     align: 'left',  getValue: (p) => p.investor_name },
  { key: 'due_date',     align: 'left',  getValue: (p) => p.payment_date },
  { key: 'type',         align: 'left',  getValue: (p) => p.narration },
  { key: 'amount',       align: 'right', getValue: (p) => p.payment_amount != null ? parseFloat(p.payment_amount) : null },
  { key: 'days_overdue', align: 'left',  getValue: (p) => p.days_overdue != null ? Number(p.days_overdue) : null },
];

function OverduePaymentsTab({
  payments = [],
  currencyCode = 'ZAR',
  showContractColumn = true,
  showInvestorColumn = false,
  showMatchActions = true,
  onMatch,
  investorNameField = 'investor_name',
}) {
  const sort = useTableSort();
  const sortedPayments = sort.sortRows(payments, OVERDUE_COLUMNS);
  if (payments.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography color="success.main" fontWeight="medium" gutterBottom>
          All payments are up to date
        </Typography>
        <Typography variant="body2" color="textSecondary">
          There are no overdue payments for this contract
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {showContractColumn && <SortableHeaderCell sort={sort} columnKey="contract">Contract</SortableHeaderCell>}
              {showInvestorColumn && <SortableHeaderCell sort={sort} columnKey="investor">Investor</SortableHeaderCell>}
              <SortableHeaderCell sort={sort} columnKey="due_date">Due Date</SortableHeaderCell>
              <SortableHeaderCell sort={sort} columnKey="type">Type</SortableHeaderCell>
              <SortableHeaderCell sort={sort} columnKey="amount" align="right">Amount</SortableHeaderCell>
              <SortableHeaderCell sort={sort} columnKey="days_overdue">Status</SortableHeaderCell>
              {showMatchActions && <TableCell align="center">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPayments.map((payment, index) => (
              <TableRow
                key={payment.id || `overdue-${index}`}
                hover
                onClick={showMatchActions && onMatch ? () => onMatch(payment) : undefined}
                sx={showMatchActions && onMatch ? { cursor: 'pointer' } : undefined}
              >
                {showContractColumn && (
                  <TableCell>
                    <ContractReferenceCell reference={payment.contract_reference || '-'} userReference={payment.user_reference} />
                  </TableCell>
                )}
                {showInvestorColumn && (
                  <TableCell>
                    <Typography variant="body2">
                      {payment[investorNameField] || '-'}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  {payment.payment_date
                    ? format(new Date(payment.payment_date), 'dd MMM yyyy')
                    : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={payment.narration || 'Payment'}
                    size="small"
                    variant="outlined"
                    color={
                      payment.narration?.includes('Interest') ? 'info' :
                      payment.narration?.includes('Capital') ? 'success' : 'default'
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium">
                    {payment.currency_code || currencyCode} {formatNumber(payment.payment_amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={payment.days_overdue ? `${payment.days_overdue} days overdue` : 'Overdue'}
                    size="small"
                    variant="outlined"
                    color="error"
                  />
                </TableCell>
                {showMatchActions && (
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    {onMatch && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onMatch(payment)}
                      >
                        Match
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default OverduePaymentsTab;
